import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useTranslation } from '../../i18n';
import API_BASE_URL from '../../config/api';

/* ========= ENV / API ========= */
const ENV_BASE =
  process.env.REACT_APP_API_BASE ||
  (typeof window !== 'undefined' && window.__CONFIG__ && window.__CONFIG__.API_BASE) ||
  `${API_BASE_URL}/api`;

const api = axios.create({
  baseURL: ENV_BASE,
  withCredentials: true,
  timeout: 15000,
});

api.interceptors.request.use((cfg) => {
  if (typeof cfg.url === 'string') cfg.url = cfg.url.replace(/^\/trainer\//, '/trainers/');
  return cfg;
});

async function searchPlans(query, signal, trainerId, includeLibrary = false) {
  const baseParams = query ? { q: query, search: query } : {};
  const mineParams = trainerId ? { trainerId, ...baseParams } : baseParams;

  const dedupe = (arr = []) => {
    const seen = new Set();
    const out = [];
    for (const p of arr) {
      const id = String(p?._id || p?.id || p?.templateId || p?.planId || '');
      if (!id || seen.has(id)) continue;
      seen.add(id);
      out.push({
        id,
        name: (p?.name || p?.title || 'Sin nombre').trim(),
        sport: p?.sport || '',
        weeks: p?.weeks,
        daysCount: p?.daysCount,
      });
    }
    return out;
  };

  try {
    const mineRes = await api.get(`/trainers/plans`, { params: mineParams, signal }).catch(() => ({ data: [] }));
    let merged = Array.isArray(mineRes.data) ? mineRes.data : (mineRes.data?.plans || []);

    if (includeLibrary) {
      const [libRes, allRes] = await Promise.all([
        api.get(`/library/plans`, { params: baseParams, signal }).catch(() => ({ data: [] })),
        api.get(`/plans`, { params: baseParams, signal }).catch(() => ({ data: [] })),
      ]);
      merged = merged
        .concat(Array.isArray(libRes.data) ? libRes.data : (libRes.data?.plans || []))
        .concat(Array.isArray(allRes.data) ? allRes.data : (allRes.data?.plans || []));
    }

    if (trainerId && !includeLibrary) {
      const TID = String(trainerId);
      merged = merged.filter((p) => {
        const owners = [
          p.trainerId, p.trainer, p.ownerId, p.owner, p.userId,
          p?.owner?._id, p?.trainer?._id, p?.createdBy, p?.createdById,
        ].map((v) => String(v || ''));
        return owners.includes(TID);
      });
    }

    return dedupe(merged);
  } catch {
    return [];
  }
}

/* ========= Normalizador de actividades ========= */
function normalizeActivitiesFromDays(input, fallbackSport = 'running') {
  const byDay = new Map();
  let maxDay = 0;

  const pushAct = (dayNum, a) => {
    const d = Math.max(1, Number(dayNum ?? a?.day ?? a?.dayIndex ?? a?.offset ?? 1));
    maxDay = Math.max(maxDay, d);
    const norm = {
      day: d,
      sport: a?.sport || fallbackSport || 'running',
      title: (a?.title || a?.name || '').trim() || '',
      metric: a?.plannedType || a?.metric || 'duration',
      value: String(a?.plannedValue ?? a?.value ?? a?.planned ?? ''),
      description: (a?.description || a?.notes || '').trim(),
    };
    if (!byDay.has(d)) byDay.set(d, []);
    byDay.get(d).push(norm);
  };

  const daysArr = (() => {
    if (!input) return [];
    if (Array.isArray(input.days)) return input.days;
    if (Array.isArray(input.weeks)) {
      const merged = [];
      input.weeks.forEach((w, wi) =>
        (w?.days || []).forEach((d, di) => {
          merged.push({ ...d, day: wi * 7 + (Number(d?.day ?? di) + 1) });
        })
      );
      return merged;
    }
    if (Array.isArray(input)) return input;
    if (Array.isArray(input.activities)) {
      input.activities.forEach((a) => pushAct(a?.day ?? a?.offset ?? a?.dayIndex ?? 1, a));
      return [];
    }
    if (input.plan?.days) return input.plan.days;
    if (input.template?.days) return input.template.days;
    return [];
  })();

  if (byDay.size > 0) return { byDay, daysCount: maxDay };

  for (const d of (Array.isArray(daysArr) ? daysArr : [])) {
    const rawDay = d?.day ?? d?.dayIndex ?? d?.offset ?? d?.index ?? d?.i ?? 1;
    const dayNum = Math.max(1, Number(rawDay) + (Number(rawDay) === 0 ? 1 : 0));
    const acts =
      Array.isArray(d?.activities) ? d.activities :
      Array.isArray(d?.workouts) ? d.workouts : [];

    if (acts.length === 0) {
      const maybeAct = d?.activity || d;
      if (maybeAct && (maybeAct.title || maybeAct.metric || maybeAct.plannedType || maybeAct.value != null)) {
        pushAct(dayNum, maybeAct);
        continue;
      }
    }

    for (const a of acts) pushAct(dayNum, a);
  }

  return { byDay, daysCount: maxDay };
}

async function fetchTemplateDetail(apiClient, tplId, fallbackSport = 'running') {
  const tryOne = async (path, pick = (r) => r?.data) => {
    try {
      const res = await apiClient.get(path);
      const payload = pick(res) ?? {};
      const norm = normalizeActivitiesFromDays(payload, fallbackSport);
      if (norm.daysCount > 0 || norm.byDay.size > 0) return norm;
    } catch {}
    return null;
  };

  const tries = [
    [`/cycles/templates/${tplId}`, (r) => r?.data],
    [`/cycles/templates/${tplId}/days`, (r) => r?.data],
    [`/cycles/templates/${tplId}/activities`, (r) => r?.data],
    [`/templates/${tplId}`, (r) => r?.data],
    [`/templates/${tplId}/days`, (r) => r?.data],
    [`/templates/${tplId}/activities`, (r) => r?.data],
    [`/plans/${tplId}`, (r) => r?.data],
    [`/plans/${tplId}/days`, (r) => r?.data],
    [`/plans/${tplId}/activities`, (r) => r?.data],
    [`/trainer/plans/${tplId}`, (r) => r?.data],
    [`/trainer/plans/${tplId}/days`, (r) => r?.data],
    [`/trainer/plans/${tplId}/activities`, (r) => r?.data],
    [`/library/plans/${tplId}`, (r) => r?.data],
    [`/library/templates/${tplId}`, (r) => r?.data],
  ];

  for (const [path, pick] of tries) {
    const norm = await tryOne(path, pick);
    if (norm) return norm;
  }

  return { byDay: new Map(), daysCount: 0 };
}

async function postWithFallback(apiClient, pathA, bodyA, pathB = null, bodyB = null) {
  try {
    return await apiClient.post(pathA, bodyA);
  } catch (err) {
    const status = err?.response?.status;
    if (pathB && (status === 400 || status === 404 || status === 405 || status === 409 || status === 422)) {
      return await apiClient.post(pathB, bodyB ?? bodyA);
    }
    throw err;
  }
}

/* ========= Utils ========= */
const mapSportEs = (s = '') => {
  const k = String(s).toLowerCase();
  if (['run', 'running', 'correr'].includes(k)) return 'correr';
  if (['bike', 'cycling', 'ciclismo'].includes(k)) return 'ciclismo';
  if (['swim', 'swimming', 'nadar', 'natación', 'natacion'].includes(k)) return 'nadar';
  if (['strength', 'fuerza', 'gym'].includes(k)) return 'fuerza';
  if (['walk', 'walking', 'caminar'].includes(k)) return 'caminar';
  if (['rest', 'descanso'].includes(k)) return 'descanso';
  if (['tri', 'triathlon', 'triatlon', 'triatlón'].includes(k)) return 'triatlon';
  return 'otro';
};

const toHHMMSS = (minutes = 60) => {
  const m = Math.max(0, parseInt(minutes, 10) || 0);
  const h = String(Math.floor(m / 60)).padStart(2, '0');
  const mm = String(m % 60).padStart(2, '0');
  return `${h}:${mm}:00`;
};

const addDays = (iso, d) => {
  const dt = new Date(`${iso}T06:00:00`);
  dt.setDate(dt.getDate() + (d - 1));
  return dt.toISOString().slice(0, 10);
};

/* ========= Componente ========= */
export default function GroupApplyPlanModal({
  open,
  onClose,
  trainerId,
  athleteIds = [],
  onApplied = () => {},
  autoCloseDelay = 700,
}) {
  const { t } = useTranslation();

  const [q, setQ] = useState('');
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [startDate, setStartDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [overwrite, setOverwrite] = useState(false);
  const [assigning, setAssigning] = useState(false);

  const [msg, setMsg] = useState('');
  const [msgKind, setMsgKind] = useState('info');

  const [manualIdMode, setManualIdMode] = useState(false);
  const [manualId, setManualId] = useState('');

  const [includeLibrary, setIncludeLibrary] = useState(false);

  useEffect(() => {
    if (!open || manualIdMode) return;
    const ctrl = new AbortController();
    (async () => {
      setLoading(true);
      try {
        const rows = await searchPlans('', ctrl.signal, trainerId, includeLibrary);
        setPlans(rows);
      } finally {
        setLoading(false);
      }
    })();
    return () => ctrl.abort();
  }, [open, manualIdMode, trainerId, includeLibrary]);

  useEffect(() => {
    if (!open || manualIdMode) return;
    const ctrl = new AbortController();
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const rows = await searchPlans(q, ctrl.signal, trainerId, includeLibrary);
        setPlans(rows);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => {
      clearTimeout(timer);
      ctrl.abort();
    };
  }, [open, q, manualIdMode, trainerId, includeLibrary]);

  useEffect(() => {
    if (!manualIdMode) return;
    const v = (manualId || '').trim();
    if (v) setSelected({ id: v, name: `ID: ${v}` });
    else setSelected(null);
  }, [manualIdMode, manualId]);

  const canApply = useMemo(
    () => !!selected && !!startDate && athleteIds.length > 0 && !assigning,
    [selected, startDate, athleteIds, assigning]
  );

  const applyServerSide = async () => {
    const payload = { templateId: selected.id, athleteIds, startDate, overwrite, trainerId };
    const res = await postWithFallback(api, `/cycles/apply-template`, payload, `/plans/instantiate`, payload);
    const d = res?.data || {};
    return {
      ok: true,
      athletes: athleteIds.length,
      createdOrUpdated: d.createdOrUpdated ?? d.created ?? 0,
      skipped: d.skipped ?? 0,
    };
  };

  const onApply = async () => {
    if (!canApply) return;
    if (athleteIds.length === 0) {
      setMsgKind('warning');
      setMsg(t('groupApplyPlan.noMembers'));
      return;
    }

    setAssigning(true);
    setMsg('');
    setMsgKind('info');

    try {
      let summary = await applyServerSide();
      if (!summary?.ok) summary = await applyClientSide();

      setMsgKind('success');
      setMsg(
        t('groupApplyPlan.successSummary', {
          athletes: summary.athletes,
          createdOrUpdated: summary.createdOrUpdated ?? 0,
          skipped: summary.skipped ? ` — ${t('groupApplyPlan.skipped')}: ${summary.skipped}` : '',
        })
      );

      window.dispatchEvent(new Event('activities:refresh'));
      window.dispatchEvent(new CustomEvent('calendar:reload', { detail: { athleteIds } }));

      try {
        onApplied(summary);
      } catch {}

      setTimeout(() => {
        try {
          onClose();
        } catch {}
      }, autoCloseDelay);
    } catch (e) {
      console.error(e);
      setMsgKind('danger');
      setMsg(t('groupApplyPlan.generalError'));
    } finally {
      setAssigning(false);
    }
  };

  async function applyClientSide() {
    const detail = await fetchTemplateDetail(api, selected.id);
    if (!detail.daysCount || detail.byDay.size === 0) throw new Error('Plantilla sin días/actividades');

    const prettyTitleFor = (act) => {
      const sport = mapSportEs(act.sport || 'correr');
      const cap = sport.charAt(0).toUpperCase() + sport.slice(1);
      if (act.metric === 'distance' && act.value) return `${cap} ${act.value} km`;
      if ((act.metric === 'duration' || !act.metric) && act.value) return `${cap} ${toHHMMSS(act.value)}`;
      return act.title || t('groupApplyPlan.session');
    };

    const tasks = [];
    for (const ath of athleteIds) {
      for (const [day, acts] of detail.byDay.entries()) {
        const targetISO = addDays(startDate, Number(day) || 1);
        acts.forEach((act) => {
          const type = mapSportEs(act.sport || 'correr');
          const prettyTitle = prettyTitleFor(act);

          const bodyCalendar = {
            athleteId: ath,
            date: targetISO,
            source: { type: 'tpl_workout', id: String(selected.id) },
            payload: {
              overrides: {
                title: prettyTitle,
                sport: type,
                plannedType: act.metric || 'duration',
                plannedValue: (act.value ?? '').toString(),
                description: act.description || '',
              },
            },
          };

          const start = new Date(`${targetISO}T06:00:00`);
          const end = new Date(start.getTime() + 60 * 60 * 1000);
          const planned = {};
          if ((act.metric === 'duration' || !act.metric) && act.value) planned.duracion = toHHMMSS(act.value);
          if (act.metric === 'distance' && act.value) planned.distancia = String(act.value);

          const bodyActivities = {
            assignedTo: ath,
            type,
            title: prettyTitle,
            trainer: trainerId || null,
            date: start,
            start,
            end,
            description: act.description || '',
            draggable: true,
            external: true,
            planned,
            completed: {},
          };

          tasks.push(postWithFallback(api, `/calendar/activities`, bodyCalendar, `/activities`, bodyActivities));
        });
      }
    }

    const results = await Promise.allSettled(tasks);
    const okCount = results.filter((r) => r.status === 'fulfilled').length;
    const fail = results.length - okCount;
    return { ok: okCount > 0, athletes: athleteIds.length, createdOrUpdated: okCount, skipped: fail };
  }

  if (!open) return null;

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.35)' }}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h6 className="modal-title">{t('groupApplyPlan.title')}</h6>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>

          <div className="modal-body">
            <div className="mb-2">
              <label className="form-label">{t('groupApplyPlan.templatePlan')}</label>

              <input
                className="form-control"
                placeholder={t('groupApplyPlan.searchPlan')}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                disabled={false}
              />

              <div className="d-flex justify-content-between align-items-center mt-1">
                <div className="form-check form-switch">
                  <input
                    id="gp-include-lib"
                    type="checkbox"
                    className="form-check-input"
                    checked={includeLibrary}
                    onChange={(e) => setIncludeLibrary(e.target.checked)}
                  />
                  <label htmlFor="gp-include-lib" className="form-check-label small">
                    {t('groupApplyPlan.includeLibrary')}
                  </label>
                </div>

                <div className="small">
                  {t('groupApplyPlan.haveIdQuestion')}{' '}
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-0"
                    onClick={() => setManualIdMode((v) => !v)}
                  >
                    {manualIdMode ? t('groupApplyPlan.backToList') : t('groupApplyPlan.enterManually')}
                  </button>
                </div>
              </div>

              {!manualIdMode && (
                <div className="border rounded mt-2" style={{ maxHeight: 240, overflow: 'auto' }}>
                  {loading && <div className="p-2 text-muted">{t('groupApplyPlan.loading')}</div>}
                  {!loading && plans.length === 0 && (
                    <div className="p-2 text-muted">{t('groupApplyPlan.noResults')}</div>
                  )}
                  {!loading &&
                    plans.map((p) => (
                      <label
                        key={p.id}
                        className={`d-flex align-items-center gap-2 p-2 border-bottom ${selected?.id === p.id ? 'bg-light' : ''}`}
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => setSelected(p)}
                      >
                        <input type="radio" className="form-check-input" checked={selected?.id === p.id} readOnly />
                        <span className="flex-grow-1">{p.name}</span>
                        {selected?.id === p.id && (
                          <span className="badge bg-primary">{t('groupApplyPlan.selected')}</span>
                        )}
                      </label>
                    ))}
                </div>
              )}

              {manualIdMode && (
                <div className="border rounded p-2">
                  <input
                    className="form-control"
                    placeholder={t('groupApplyPlan.idPlaceholder')}
                    value={manualId}
                    onChange={(e) => setManualId(e.target.value)}
                  />
                </div>
              )}
            </div>

            <div className="row g-3">
              <div className="col-12 col-md-6">
                <label className="form-label">{t('groupApplyPlan.startDate')}</label>
                <input
                  type="date"
                  className="form-control"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className="col-12 col-md-6 d-flex align-items-end">
                <div className="form-check">
                  <input
                    id="gp-overwrite"
                    type="checkbox"
                    className="form-check-input"
                    checked={overwrite}
                    onChange={(e) => setOverwrite(e.target.checked)}
                  />
                  <label htmlFor="gp-overwrite" className="form-check-label ms-1">
                    {t('groupApplyPlan.overwrite')}
                  </label>
                </div>
              </div>
            </div>

            {msg && (
              <div
                className={`alert mt-3 mb-0 ${
                  msgKind === 'success'
                    ? 'alert-success'
                    : msgKind === 'danger'
                    ? 'alert-danger'
                    : msgKind === 'warning'
                    ? 'alert-warning'
                    : 'alert-info'
                }`}
              >
                {msg}
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={assigning}>
              {t('common.cancel')}
            </button>
            <button type="button" className="btn btn-primary" onClick={onApply} disabled={!canApply}>
              {assigning ? t('groupApplyPlan.loading') : t('groupApplyPlan.applyToGroup')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}